import {
  cadastroClienteSchema,
  cnpjValido,
  cpfValido,
  UFS,
  type CadastroClienteInput,
  type CadastroPublicoDTO,
} from '@viniko/shared';
import { CheckCircle2, Clock, Loader2 } from 'lucide-react';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import './cadastro.css';

type Campo = keyof CadastroClienteInput;

const VAZIO: CadastroClienteInput = {
  tipoPessoa: 'J',
  nome: '',
  fantasia: '',
  documento: '',
  ie: '',
  rg: '',
  telefone: '',
  celular: '',
  email: '',
  cep: '',
  municipio: '',
  uf: '' as CadastroClienteInput['uf'],
  endereco: '',
  bairro: '',
  numero: '',
  complemento: '',
};

const so = (s: string) => s.replace(/\D/g, '');

function mascara(campo: Campo, v: string, tipo: 'J' | 'F'): string {
  const d = so(v);
  if (campo === 'documento' && tipo === 'J')
    return d
      .slice(0, 14)
      .replace(/^(\d{2})(\d)/, '$1.$2')
      .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
      .replace(/\.(\d{3})(\d)/, '.$1/$2')
      .replace(/(\d{4})(\d)/, '$1-$2');
  if (campo === 'documento')
    return d
      .slice(0, 11)
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d)/, '$1.$2')
      .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
  if (campo === 'cep') return d.slice(0, 8).replace(/^(\d{5})(\d)/, '$1-$2');
  if (campo === 'celular' || campo === 'telefone')
    return d
      .slice(0, 11)
      .replace(/^(\d{2})(\d)/, '($1) $2')
      .replace(/(\d)(\d{4})$/, '$1-$2');
  return v;
}

interface ReceitaCnpj {
  razao_social?: string;
  nome_fantasia?: string;
  cep?: string;
  descricao_tipo_de_logradouro?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  municipio?: string;
  uf?: string;
}

interface ViaCep {
  erro?: boolean;
  logradouro?: string;
  bairro?: string;
  localidade?: string;
  uf?: string;
}

const MINUSCULAS = new Set(['de', 'da', 'do', 'das', 'dos', 'e']);
const titulo = (s = '') =>
  s
    .toLowerCase()
    .split(' ')
    .map((p, i) => (i && MINUSCULAS.has(p) ? p : p.charAt(0).toUpperCase() + p.slice(1)))
    .join(' ');

async function buscarJson<T>(url: string): Promise<T | null> {
  try {
    const r = await fetch(url, { signal: AbortSignal.timeout(8000) });
    return r.ok ? ((await r.json()) as T) : null;
  } catch {
    return null;
  }
}

export function CadastroPublico() {
  const { codigo = '' } = useParams();
  const [info, setInfo] = useState<CadastroPublicoDTO | 'nao-existe' | null>(null);
  const [form, setForm] = useState<CadastroClienteInput>(VAZIO);
  const [erros, setErros] = useState<Partial<Record<Campo, string>>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [buscando, setBuscando] = useState<'cnpj' | 'cep' | null>(null);

  useEffect(() => {
    document.title = 'Cadastro · Viniko';
    api<CadastroPublicoDTO>(`/api/publico/cadastro/${encodeURIComponent(codigo)}`)
      .then((d) => {
        setInfo(d);
        setForm((f) => ({ ...f, celular: d.celular }));
      })
      .catch(() => setInfo('nao-existe'));
  }, [codigo]);

  const mudar = (campo: Campo, valor: string) => {
    setForm((f) => ({ ...f, [campo]: mascara(campo, valor, f.tipoPessoa) }));
    setErros((e) => ({ ...e, [campo]: undefined }));
  };

  const preencherVazios = (dados: Partial<CadastroClienteInput>) =>
    setForm((f) => {
      const novo = { ...f };
      for (const [k, v] of Object.entries(dados) as [Campo, string][])
        if (v && !String(f[k] ?? '').trim()) (novo as Record<Campo, string>)[k] = v;
      return novo;
    });

  async function aoSairDoDocumento() {
    if (form.tipoPessoa !== 'J' || so(form.documento).length !== 14) return;
    setBuscando('cnpj');
    const r = await buscarJson<ReceitaCnpj>(
      `https://brasilapi.com.br/api/cnpj/v1/${so(form.documento)}`,
    );
    // A Receita manda sem acento; o ViaCEP tem rua, bairro e cidade acentuados
    const cep =
      r?.cep && so(r.cep).length === 8
        ? await buscarJson<ViaCep>(`https://viacep.com.br/ws/${so(r.cep)}/json/`)
        : null;
    const viaCep = cep && !cep.erro ? cep : null;
    setBuscando(null);
    if (!r) return;
    preencherVazios({
      nome: r.razao_social,
      fantasia: r.nome_fantasia,
      cep: mascara('cep', r.cep ?? '', 'J'),
      endereco:
        viaCep?.logradouro ||
        titulo([r.descricao_tipo_de_logradouro, r.logradouro].filter(Boolean).join(' ')),
      numero: r.numero,
      complemento: titulo(r.complemento),
      bairro: viaCep?.bairro || titulo(r.bairro),
      municipio: viaCep?.localidade || titulo(r.municipio),
      uf: r.uf as CadastroClienteInput['uf'],
    });
  }

  async function aoSairDoCep() {
    if (so(form.cep).length !== 8) return;
    setBuscando('cep');
    const r = await buscarJson<ViaCep>(`https://viacep.com.br/ws/${so(form.cep)}/json/`);
    setBuscando(null);
    if (!r || r.erro) return setErros((e) => ({ ...e, cep: 'CEP não encontrado' }));
    preencherVazios({
      endereco: r.logradouro,
      bairro: r.bairro,
      municipio: r.localidade,
      uf: r.uf as CadastroClienteInput['uf'],
    });
  }

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setErroGeral(null);
    const r = cadastroClienteSchema.safeParse(form);
    if (!r.success) {
      const pj = form.tipoPessoa === 'J';
      const docOk = pj ? cnpjValido(form.documento) : cpfValido(form.documento);
      const novos: Partial<Record<Campo, string>> = docOk
        ? {}
        : {
            documento: so(form.documento)
              ? `${pj ? 'CNPJ' : 'CPF'} inválido`
              : `Preencha o ${pj ? 'CNPJ' : 'CPF'}`,
          };
      for (const i of r.error.issues) novos[i.path[0] as Campo] ??= i.message;
      setErros(novos);
      document.getElementById(`cad-${Object.keys(novos)[0]}`)?.focus();
      return;
    }
    setEnviando(true);
    try {
      await api(`/api/publico/cadastro/${encodeURIComponent(codigo)}`, {
        metodo: 'POST',
        corpo: form,
      });
      setInfo({ estado: 'enviado', nome: '', celular: '' });
      window.scrollTo({ top: 0 });
    } catch (err) {
      setErroGeral((err as Error).message);
    } finally {
      setEnviando(false);
    }
  }

  if (!info)
    return (
      <Moldura>
        <p className="cad-aviso">
          <Loader2 className="girando" /> Carregando…
        </p>
      </Moldura>
    );
  if (info === 'nao-existe' || info.estado === 'vencido')
    return (
      <Moldura>
        <div className="cad-fim">
          <Clock />
          <h2>Este link não vale mais</h2>
          <p>Ele venceu ou foi trocado por um novo. Peça outro link pra gente no WhatsApp.</p>
        </div>
      </Moldura>
    );
  if (info.estado === 'enviado')
    return (
      <Moldura>
        <div className="cad-fim ok">
          <CheckCircle2 />
          <h2>Cadastro recebido!</h2>
          <p>Obrigado. Já pode voltar pro WhatsApp, a gente continua por lá.</p>
        </div>
      </Moldura>
    );

  const pj = form.tipoPessoa === 'J';
  const campo = (
    nome: Campo,
    rotulo: string,
    extra: {
      obrigatorio?: boolean;
      dica?: string;
      tipo?: string;
      modo?: 'numeric' | 'email' | 'tel' | 'text';
      auto?: string;
      aoSair?: () => void;
      carregando?: boolean;
    } = {},
  ) => (
    <label className={`campo cad-campo ${erros[nome] ? 'com-erro' : ''}`}>
      <span>
        {rotulo}
        {extra.obrigatorio && <b aria-hidden> *</b>}
      </span>
      {extra.dica && <small>{extra.dica}</small>}
      <span className="cad-input">
        <input
          id={`cad-${nome}`}
          className="input"
          type={extra.tipo ?? 'text'}
          inputMode={extra.modo}
          autoComplete={extra.auto ?? 'off'}
          value={String(form[nome] ?? '')}
          onChange={(e) => mudar(nome, e.target.value)}
          onBlur={extra.aoSair}
          aria-invalid={!!erros[nome]}
          required={extra.obrigatorio}
        />
        {extra.carregando && <Loader2 className="girando" />}
      </span>
      {erros[nome] && <em className="erro-form">{erros[nome]}</em>}
    </label>
  );

  return (
    <Moldura>
      <form className="cad-form" onSubmit={enviar} noValidate>
        <p className="cad-intro">
          Preencha os dados pra gente emitir o pedido e a nota fiscal. Leva uns 2 minutos.
        </p>

        <fieldset className="cad-tipo">
          <legend>
            Tipo de pessoa <b aria-hidden>*</b>
          </legend>
          {(
            [
              ['J', 'Jurídica (CNPJ)'],
              ['F', 'Física (CPF)'],
            ] as const
          ).map(([v, r]) => (
            <label key={v} className={form.tipoPessoa === v ? 'ativo' : ''}>
              <input
                type="radio"
                name="tipoPessoa"
                checked={form.tipoPessoa === v}
                onChange={() => {
                  setForm((f) => ({ ...f, tipoPessoa: v, documento: '' }));
                  setErros({});
                }}
              />
              {r}
            </label>
          ))}
        </fieldset>

        <Secao titulo="Dados">
          {pj
            ? campo('documento', 'CNPJ', {
                obrigatorio: true,
                modo: 'numeric',
                dica: 'Preenchemos o resto com os dados da Receita',
                aoSair: () => void aoSairDoDocumento(),
                carregando: buscando === 'cnpj',
              })
            : campo('documento', 'CPF', { obrigatorio: true, modo: 'numeric' })}
          {campo('nome', pj ? 'Razão social' : 'Nome completo', {
            obrigatorio: true,
            auto: pj ? 'organization' : 'name',
          })}
          {pj && campo('fantasia', 'Nome fantasia')}
          {pj
            ? campo('ie', 'Inscrição estadual', { dica: 'Necessário caso tenha' })
            : campo('rg', 'RG')}
        </Secao>

        <Secao titulo="Contato">
          <div className="cad-linha">
            {campo('celular', 'Celular', { obrigatorio: true, modo: 'tel', auto: 'tel' })}
            {campo('telefone', 'Telefone fixo', { modo: 'tel' })}
          </div>
          {campo('email', 'E-mail', {
            obrigatorio: true,
            tipo: 'email',
            modo: 'email',
            auto: 'email',
            dica: 'A nota fiscal vai pra esse e-mail',
          })}
        </Secao>

        <Secao titulo="Endereço">
          <div className="cad-linha">
            {campo('cep', 'CEP', {
              obrigatorio: true,
              modo: 'numeric',
              auto: 'postal-code',
              aoSair: () => void aoSairDoCep(),
              carregando: buscando === 'cep',
            })}
            <span />
          </div>
          {campo('endereco', 'Endereço', { obrigatorio: true, dica: 'Rua, avenida…' })}
          <div className="cad-linha">
            {campo('numero', 'Número', { obrigatorio: true })}
            {campo('complemento', 'Complemento')}
          </div>
          {campo('bairro', 'Bairro', { obrigatorio: true })}
          <div className="cad-linha cad-cidade">
            {campo('municipio', 'Município', { obrigatorio: true })}
            <label className={`campo cad-campo ${erros.uf ? 'com-erro' : ''}`}>
              <span>
                UF<b aria-hidden> *</b>
              </span>
              <select
                id="cad-uf"
                className="input"
                value={form.uf}
                onChange={(e) => mudar('uf', e.target.value)}
                aria-invalid={!!erros.uf}
              >
                <option value="">—</option>
                {UFS.map((u) => (
                  <option key={u}>{u}</option>
                ))}
              </select>
              {erros.uf && <em className="erro-form">{erros.uf}</em>}
            </label>
          </div>
        </Secao>

        {erroGeral && <p className="erro-form cad-erro-geral">{erroGeral}</p>}
        <button className="btn primario cad-enviar" disabled={enviando}>
          {enviando ? 'Enviando…' : 'Enviar cadastro'}
        </button>
        <p className="cad-rodape">Seus dados ficam só com a Viniko, pra pedidos e notas fiscais.</p>
      </form>
    </Moldura>
  );
}

const Secao = ({ titulo, children }: { titulo: string; children: ReactNode }) => (
  <section className="cad-secao">
    <h2>{titulo}</h2>
    {children}
  </section>
);

const Moldura = ({ children }: { children: ReactNode }) => (
  <main className="cad">
    <div className="cad-cartao">
      <header className="cad-topo">
        <img
          className="cad-logo"
          src="/logo-viniko.webp"
          alt="Viniko Loja"
          width={600}
          height={358}
        />
        <h1>Cadastro de cliente</h1>
      </header>
      {children}
    </div>
  </main>
);
